import React, { ChangeEvent, useState } from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, Flex, Input, Text } from '@opendoor/bricks/core';

import { OdProtosListingsData_Listing_State as ListingState } from '../../__generated__/athenaGlobalTypes';
import { useSetListingDetails } from '../../graphql/athena/mutations/SetListingDetails';
import { isValidDateForLwc } from '../../routes/LwcDashboard/utils';

type ListingActiveFormProps = {
  listingId: string;
};

/** Uses the attempt first contact mutation to transition the listing state and update the time */
export const ListingActiveForm: React.FC<ListingActiveFormProps> = ({ listingId }) => {
  const [date, setDate] = useState('');
  const [listingPriceInput, setListingPriceInput] = useState({
    listing_price: 0,
  });
  const isValidDate = isValidDateForLwc(date);
  const isoDate = isValidDate ? new Date(date).toISOString() : '';

  const { setListingDetails, error, loading } = useSetListingDetails();

  const handleSetListingPrice = async (listingPrice: number, date: string) => {
    setListingDetails({
      params: {
        id: listingId,
        listingPrice: listingPrice,
        nextState: ListingState.LISTING_ACTIVE,
        updateMask: {
          paths: ['listing_price', 'next_state', 'timestamp_overrides'],
        },
        timestampOverrides: [
          {
            state: ListingState.LISTING_ACTIVE,
            timestamp: date,
          },
        ],
      },
    })
      .then(() => {
        showToast('info', `Successfully set listing details`, 'lwc-dashboard-toast}', {
          toastOptions: { autoClose: 3000 },
        });
      })
      .catch((_) => {
        // Just catch for now, log later
      });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputDate = e.target.value;
    setDate(inputDate);
  };
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setListingPriceInput((prevFormData) => ({
      ...prevFormData,
      // The input takes in a string, but we want to send a number to the backend
      [name]: Number.parseInt(value),
    }));
  };

  if (error) {
    showToast('error', 'Error setting listing price', 'lwc-dashboard-toast', {
      toastOptions: { autoClose: false },
    });
  }

  return (
    <Flex flexDirection="column">
      <Text>Date home was listed</Text>
      <input type="date" value={date} onChange={handleDateChange} />
      <Input
        id="listing_price"
        name="listing_price"
        type="text"
        label="Listing price"
        orientation="vertical"
        value={listingPriceInput.listing_price?.toString()}
        placeholder="$123,456"
        onChange={(e) => e && handleInputChange(e)}
        analyticsName="listing-price-input"
      />
      <Button
        my={3}
        aria-label="Submit when listing agreement was signed"
        analyticsName="set-listing-agreement-signed-at"
        onClick={() => handleSetListingPrice(listingPriceInput.listing_price, isoDate)}
        disabled={!isValidDate || !listingPriceInput.listing_price}
        loading={loading}
      >
        Set listing price
      </Button>
    </Flex>
  );
};
